import React, { useEffect, useMemo, useState } from "react";
import { Collapse, Icon } from "antd";
import { change as changeField, Field, FormSection } from "redux-form";
import { useDispatch } from "react-redux";

import styles from "../../../../../JobStyle.module.css";
import layoutStyles from "../../../../../../SwmsMasters/EmergencyPlans/EmergencyPlansLayout.module.css";
import JobSwmsV3EmergencyProcedureDetailsSection from "./fixed/JobSwmsV3EmergencyProcedureDetailsSection";
import JobSwmsV3EmergencyProcedureDocumentAuthorizationSection from "./fixed/JobSwmsV3EmergencyProcedureDocumentAuthorizationSection";
import JobSwmsV3EmergencyProcedureRichTextSection from "./sections/JobSwmsV3EmergencyProcedureRichTextSection";
import JobSwmsV3EmergencyProcedureRescueConfigurationMediaSection from "./sections/JobSwmsV3EmergencyProcedureRescueConfigurationMediaSection";
import PerformRescueSection from "../../../../../../SwmsMasters/EmergencyProcedures/sections/section-types/PerformRescueSection";
import procedureStyles from "../JobSwmsV3EmergencyProceduresContainer.module.css";
import { CustomSwitch } from "../../../../../../../common/customSwitch";
import AddCustomEmergencyProcedureSectionModal from "../../../../../../SwmsMasters/EmergencyProcedures/sections/custom/AddCustomEmergencyProcedureSectionModal";
import EditCustomEmergencyProcedureHeadingModal from "../../../../../../SwmsMasters/EmergencyProcedures/sections/custom/EditCustomEmergencyProcedureHeadingModal";

const { Panel } = Collapse;
const FORM_NAME = "JobSwmsV3EmergencyProceduresContainerForm";

const SECTION_LABELS = {
  procedure_application: "Procedure Application",
  use_when: "Use This Procedure When",
  do_not_use_when: "Do Not Use This Procedure When",
  rescue_configuration_media: "Rescue Configuration Photos & Attachments",
  perform_rescue: "Perform the Rescue",
  stop_escalate: "Stop & Escalate",
  after_recovery: "After Recovery"
};

const getSectionLabel = (sectionKey, section = {}) => (
  section.display_name || SECTION_LABELS[sectionKey] || sectionKey
);

export default function JobSwmsV3EmergencyProcedureSectionRegistry({
  procedure,
  procedureIndex,
  swmsData,
  status,
  loading
}) {
  const dispatch = useDispatch();
  const sections = procedure && procedure.sections && typeof procedure.sections === "object"
    ? procedure.sections
    : {};
  const sourceOrder = procedure && Array.isArray(procedure.section_order)
    ? procedure.section_order
    : Object.keys(sections);
  const [sectionOrder, setSectionOrder] = useState(sourceOrder);
  const [draggingSectionKey, setDraggingSectionKey] = useState(null);
  const [addCustomSectionModalOpen, setAddCustomSectionModalOpen] = useState(false);
  const [editCustomHeadingModalOpen, setEditCustomHeadingModalOpen] = useState(false);
  const [editingCustomSectionKey, setEditingCustomSectionKey] = useState(null);

  useEffect(() => {
    setSectionOrder(sourceOrder);
  }, [sourceOrder.join("|")]);

  const orderedSections = useMemo(
    () => sectionOrder.filter((sectionKey) => sectionKey !== "procedure_application" && sections[sectionKey]),
    [sectionOrder, sections]
  );
  const documentAuthorizationData = swmsData
    && swmsData.sections
    && swmsData.sections.document_authorization
    && swmsData.sections.document_authorization.data
    ? swmsData.sections.document_authorization.data
    : {};

  const moveSection = (sourceKey, targetKey) => {
    if (!sourceKey || !targetKey || sourceKey === targetKey) return;

    setSectionOrder((currentOrder) => {
      const sourceIndex = currentOrder.indexOf(sourceKey);
      const targetIndex = currentOrder.indexOf(targetKey);
      if (sourceIndex < 0 || targetIndex < 0) return currentOrder;

      const nextOrder = [...currentOrder];
      const [moved] = nextOrder.splice(sourceIndex, 1);
      nextOrder.splice(targetIndex, 0, moved);
      dispatch(changeField(
        FORM_NAME,
        `procedures[${procedureIndex}].section_order`,
        nextOrder
      ));
      return nextOrder;
    });
  };

  const isCustomSection = (section = {}) => Boolean(
    section.type === "custom" || Number(section.data && section.data.is_custom) === 1
  );

  const onAddCustomSection = ({ heading, bodyHtml }) => {
    const uniqueSectionId = `custom_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
    const nextSections = {
      ...sections,
      [uniqueSectionId]: {
        id: uniqueSectionId,
        type: "custom",
        display_name: heading,
        data: { content: { html: bodyHtml || "" }, toggle: 1, is_custom: 1 }
      }
    };
    const nextOrder = [...(Array.isArray(procedure.section_order) ? procedure.section_order : []), uniqueSectionId];
    dispatch(changeField(FORM_NAME, `procedures[${procedureIndex}].sections`, nextSections));
    dispatch(changeField(FORM_NAME, `procedures[${procedureIndex}].section_order`, nextOrder));
    setAddCustomSectionModalOpen(false);
  };

  const onDeleteCustomSection = (sectionKey) => {
    const nextSections = { ...sections };
    delete nextSections[sectionKey];
    const nextOrder = (Array.isArray(procedure.section_order) ? procedure.section_order : [])
      .filter((key) => key !== sectionKey);
    dispatch(changeField(FORM_NAME, `procedures[${procedureIndex}].sections`, nextSections));
    dispatch(changeField(FORM_NAME, `procedures[${procedureIndex}].section_order`, nextOrder));
  };

  const onSaveCustomSectionHeading = (nextHeading) => {
    if (!editingCustomSectionKey) return;
    dispatch(changeField(
      FORM_NAME,
      `procedures[${procedureIndex}].sections.${editingCustomSectionKey}.display_name`,
      nextHeading
    ));
    setEditCustomHeadingModalOpen(false);
    setEditingCustomSectionKey(null);
  };

  return (
    <div className="container-fluid py-3">
      <div className="row">
        <div className="col-12">
          <Collapse
            accordion
            defaultActiveKey="procedure-details"
            bordered={false}
            expandIconPosition="right"
            className="custom-section-v2-collapse custom-job-swms-collapse swms-masters-action-collapse"
            expandIcon={({ isActive }) => (
              <Icon type="caret-right" rotate={isActive ? 90 : 0} style={{ color: "#2f1f41" }} />
            )}
          >
            {JobSwmsV3EmergencyProcedureDetailsSection({
              procedure,
              procedureIndex,
              swmsData,
              status,
              loading
            })}
            {JobSwmsV3EmergencyProcedureDocumentAuthorizationSection({
              procedureIndex,
              loading,
              approvers: Array.isArray(documentAuthorizationData.approvers)
                ? documentAuthorizationData.approvers
                : []
            })}
            {orderedSections.map((sectionKey) => {
              const sectionData = sections[sectionKey] || {};
              const customSection = isCustomSection(sectionData);
              const isDisabled = Number(sectionData.data && sectionData.data.toggle) === 0;
              return (
              <Panel
                key={sectionKey}
                className="custom-job-swmsv3-collapse-panel"
                header={(
                  <div
                    className={`${styles.BackgroundSectionHeading} custom-section-v2-panel-heading`}
                    draggable={!loading}
                    onDragStart={(event) => {
                      setDraggingSectionKey(sectionKey);
                      if (event.dataTransfer) {
                        event.dataTransfer.effectAllowed = "move";
                        event.dataTransfer.setData("text/plain", sectionKey);
                      }
                    }}
                    onDragEnd={() => setDraggingSectionKey(null)}
                    onDragEnter={() => {
                      if (draggingSectionKey && draggingSectionKey !== sectionKey) {
                        moveSection(draggingSectionKey, sectionKey);
                      }
                    }}
                    onDragOver={(event) => {
                      if (draggingSectionKey && draggingSectionKey !== sectionKey) {
                        event.preventDefault();
                      }
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: loading ? "default" : "grab"
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{ display: "inline-flex", color: "#362148", opacity: 0.8 }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" width="14" height="14">
                        <path
                          fill="currentColor"
                          d="M40 352l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zm192 0l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 320c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zm192-128l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 160c-22.1 0-40-17.9-40-40L0 72C0 49.9 17.9 32 40 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zm192-128l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40z"
                        />
                      </svg>
                    </span>
                    <span>{getSectionLabel(sectionKey, sectionData)}</span>
                    {customSection ? (
                      <span
                        className={`float-right position-relative ${layoutStyles.SectionPanelActions}`}
                        style={{ opacity: isDisabled ? 0.55 : 1 }}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        onMouseDown={(event) => event.stopPropagation()}
                      >
                        <button
                          type="button"
                          disabled={loading}
                          className={`${layoutStyles.SectionPanelIconButton} ${layoutStyles.SectionPanelEditButton}`}
                          title="Edit section heading"
                          aria-label="Edit section heading"
                          onClick={() => {
                            setEditingCustomSectionKey(sectionKey);
                            setEditCustomHeadingModalOpen(true);
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" width="16" height="16" aria-hidden="true">
                            <path fill="currentColor" d="M416.9 85.2L372 130.1L509.9 268L554.8 223.1C568.4 209.6 576 191.2 576 172C576 152.8 568.4 134.4 554.8 120.9L519.1 85.2C505.6 71.6 487.2 64 468 64C448.8 64 430.4 71.6 416.9 85.2zM338.1 164L122.9 379.1C112.2 389.8 104.4 403.2 100.3 417.8L64.9 545.6C62.6 553.9 64.9 562.9 71.1 569C77.3 575.1 86.2 577.5 94.5 575.2L222.3 539.7C236.9 535.6 250.2 527.9 261 517.1L476 301.9L338.1 164z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          disabled={loading}
                          className={`${layoutStyles.SectionPanelIconButton} ${layoutStyles.SectionPanelDeleteButton}`}
                          title="Delete section"
                          aria-label="Delete section"
                          onClick={() => onDeleteCustomSection(sectionKey)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="16" height="16" aria-hidden="true">
                            <path fill="currentColor" d="M136.7 5.9L128 32 32 32C14.3 32 0 46.3 0 64S14.3 96 32 96l384 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-96 0-8.7-26.1C306.9-7.2 294.7-16 280.9-16L167.1-16c-13.8 0-26 8.8-30.4 21.9zM416 144L32 144 53.1 467.1C54.7 492.4 75.7 512 101 512L347 512c25.3 0 46.3-19.6 47.9-44.9L416 144z" />
                          </svg>
                        </button>
                        <Field
                          name={`procedures[${procedureIndex}].sections.${sectionKey}.data.toggle`}
                          component={CustomSwitch}
                          size="small"
                          disabled={loading}
                        />
                      </span>
                    ) : null}
                    {sectionKey === "rescue_configuration_media"
                      && sections[sectionKey].data
                      && Array.isArray(sections[sectionKey].data.attachments) ? (
                        <span
                          className={layoutStyles.SectionPanelItemCountChip}
                          style={{ background: "#E7E0FF", color: "#4B2C79" }}
                          title={`${sections[sectionKey].data.attachments.length} item${sections[sectionKey].data.attachments.length === 1 ? "" : "s"}`}
                          aria-label={`${sections[sectionKey].data.attachments.length} item${sections[sectionKey].data.attachments.length === 1 ? "" : "s"}`}
                        >
                          {sections[sectionKey].data.attachments.length}
                        </span>
                      ) : null}
                    {sectionKey === "perform_rescue"
                      && sections[sectionKey].data
                      && Array.isArray(sections[sectionKey].data.phases) ? (
                        <span
                          className={layoutStyles.SectionPanelItemCountChip}
                          style={{ background: "#E7E0FF", color: "#4B2C79" }}
                          title={`${sections[sectionKey].data.phases.length} phase${sections[sectionKey].data.phases.length === 1 ? "" : "s"}`}
                          aria-label={`${sections[sectionKey].data.phases.length} phase${sections[sectionKey].data.phases.length === 1 ? "" : "s"}`}
                        >
                          {sections[sectionKey].data.phases.length}
                        </span>
                      ) : null}
                    {!customSection && ["use_when", "do_not_use_when", "rescue_configuration_media", "perform_rescue", "stop_escalate", "after_recovery"].includes(sectionKey) ? (
                      <span
                        className="float-right position-relative"
                        style={{ marginLeft: "auto", marginRight: "30px" }}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        onMouseDown={(event) => event.stopPropagation()}
                      >
                        <Field
                          name={`procedures[${procedureIndex}].sections.${sectionKey}.data.toggle`}
                          component={CustomSwitch}
                          size="small"
                          disabled={loading}
                        />
                      </span>
                    ) : null}
                  </div>
                )}
              >
                {customSection ? (
                  <JobSwmsV3EmergencyProcedureRichTextSection
                    procedure={procedure}
                    procedureIndex={procedureIndex}
                    sectionKey={sectionKey}
                    label={getSectionLabel(sectionKey, sectionData)}
                    loading={loading}
                  />
                ) : ["use_when", "do_not_use_when", "stop_escalate", "after_recovery"].includes(sectionKey) ? (
                  <JobSwmsV3EmergencyProcedureRichTextSection
                    procedure={procedure}
                    procedureIndex={procedureIndex}
                    sectionKey={sectionKey}
                    label={getSectionLabel(sectionKey, sections[sectionKey])}
                    loading={loading}
                  />
                ) : sectionKey === "rescue_configuration_media" ? (
                  <JobSwmsV3EmergencyProcedureRescueConfigurationMediaSection
                    procedureIndex={procedureIndex}
                    loading={loading}
                  />
                ) : sectionKey === "perform_rescue" ? (
                  <FormSection
                    component={PerformRescueSection}
                    name={`procedures[${procedureIndex}].sections.perform_rescue.data`}
                    loading={loading}
                  />
                ) : (
                  <div className={procedureStyles.SectionPlaceholder} />
                )}
              </Panel>
              );
            })}
          </Collapse>
        </div>
      </div>
      <button
        type="button"
        disabled={loading}
        onClick={() => setAddCustomSectionModalOpen(true)}
        className={layoutStyles.AddSectionButton}
      >
        Add Section
      </button>
      <AddCustomEmergencyProcedureSectionModal
        visible={addCustomSectionModalOpen}
        loading={loading}
        onCancel={() => setAddCustomSectionModalOpen(false)}
        onAdd={onAddCustomSection}
      />
      <EditCustomEmergencyProcedureHeadingModal
        visible={editCustomHeadingModalOpen}
        loading={loading}
        initialHeading={editingCustomSectionKey && sections[editingCustomSectionKey]
          ? sections[editingCustomSectionKey].display_name || ""
          : ""}
        onCancel={() => {
          setEditCustomHeadingModalOpen(false);
          setEditingCustomSectionKey(null);
        }}
        onSave={onSaveCustomSectionHeading}
      />
    </div>
  );
}
